import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, type Nachfrage as NachfrageDaten, type Notiz, type NotizFilter } from '../api'
import { useAufnahme, type FertigeAufnahme } from '../aufnahme'
import { link } from '../navigation'
import { nachTagen } from '../tagebuch'
import { abarbeiten, warteschlange, type Wartend } from '../warteschlange'
import { AufnahmeLeiste } from './AufnahmeLeiste'
import { Nachfrage } from './Nachfrage'
import { NotizKarte, WartendKarte } from './NotizKarte'
import { TextEingabe } from './TextEingabe'

const IN_ARBEIT = new Set(['TRANSKRIPTION', 'AUSWERTUNG'])
const eigeneZeitzone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

/** Fuehrt neu geladene Notizen mit den bereits angezeigten (inkl. aelterer Seiten) zusammen. */
function zusammenfuehren(alt: Notiz[], neu: Notiz[]): Notiz[] {
  const nachId = new Map(alt.map((n) => [n.id, n]))
  for (const n of neu) nachId.set(n.id, n)
  return [...nachId.values()].sort((a, b) => b.aufgenommenAm.localeCompare(a.aufgenommenAm))
}

/** #tagebuch/tag/<name> bzw. #tagebuch/person/<id> filtern das Tagebuch, #tagebuch/archiv zeigt das Archiv */
function filterAusPfad(pfad: string[]): NotizFilter {
  if (pfad[0] === 'archiv') return { archiv: true }
  if (pfad[0] === 'tag' && pfad[1]) return { tag: pfad[1] }
  if (pfad[0] === 'person' && Number(pfad[1])) return { personId: Number(pfad[1]) }
  return {}
}

export function Tagebuch({
  kiBereit,
  pfad,
  neueGeteilte,
  onAusgewertet,
  sofortAufnehmen,
  onAufnehmenGestartet,
}: {
  kiBereit: boolean
  pfad: string[]
  neueGeteilte: number
  onAusgewertet: () => void
  /** Der Kurzlink #aufnehmen wurde geöffnet: Aufnahme starten, dann [onAufnehmenGestartet] melden */
  sofortAufnehmen: boolean
  onAufnehmenGestartet: () => void
}) {
  const filterSchluessel = pfad.join('/')
  const filter = useMemo(() => filterAusPfad(pfad), [filterSchluessel])
  const [filterName, setFilterName] = useState<string | null>(null)
  const [notizen, setNotizen] = useState<Notiz[]>([])
  const [weitere, setWeitere] = useState(false)
  const [wartend, setWartend] = useState<Wartend[]>([])
  const [geladen, setGeladen] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [tippen, setTippen] = useState(false)
  // Nachfrage der App, auf die gerade geantwortet wird (Sprache bereit oder Texteingabe offen)
  const [antwort, setAntwort] = useState<NachfrageDaten | null>(null)
  const [nachfrageVersion, setNachfrageVersion] = useState(0)

  // Antworten zu einem inzwischen gewechselten Filter werden verworfen
  const aktuellerFilter = useRef(filter)
  aktuellerFilter.current = filter

  // Filterwechsel: von vorn laden
  useEffect(() => {
    setNotizen([])
    setWeitere(false)
    setGeladen(false)
    setFilterName(filter.tag ? `#${filter.tag}` : filter.archiv ? 'Archiv' : null)
    if (filter.personId) api.person(filter.personId).then((p) => setFilterName(p.person.name)).catch(() => setFilterName('unbekannte Person'))
  }, [filter])

  const neuLaden = useCallback(async () => {
    try {
      const seite = await api.notizen(undefined, filter)
      if (aktuellerFilter.current !== filter) return
      setNotizen((alt) => zusammenfuehren(alt, seite.notizen))
      setWeitere((w) => w || seite.weitere)
      setFehler(null)
    } catch (e) {
      setFehler((e as Error).message)
    } finally {
      setGeladen(true)
    }
  }, [filter])

  const hochladen = useCallback(async () => {
    const anzahl = await abarbeiten().catch(() => 0)
    setWartend(await warteschlange.alle())
    if (anzahl > 0) await neuLaden()
  }, [neuLaden])

  useEffect(() => {
    neuLaden()
    hochladen()
    window.addEventListener('online', hochladen)
    return () => window.removeEventListener('online', hochladen)
  }, [neuLaden, hochladen])

  // Solange etwas hochgeladen oder transkribiert wird, regelmaessig nachsehen
  const beschaeftigt = wartend.some((w) => !w.fehler) || notizen.some((n) => IN_ARBEIT.has(n.status))
  useEffect(() => {
    if (!beschaeftigt) return
    const t = setInterval(() => {
      hochladen()
      neuLaden()
      // ausgewertete Notizen koennen neue Rueckfragen erzeugt haben
      onAusgewertet()
    }, 3000)
    return () => clearInterval(t)
  }, [beschaeftigt, hochladen, neuLaden, onAusgewertet])

  // Die Aufnahme-Funktion lebt über Renderings hinweg, deshalb der Zugriff über eine Referenz
  const antwortRef = useRef<NachfrageDaten | null>(null)
  antwortRef.current = antwort

  const aufnahme = useAufnahme(
    useCallback(
      async (a: FertigeAufnahme) => {
        await warteschlange.ablegen({
          clientId: crypto.randomUUID(),
          audio: a.audio,
          typ: a.typ,
          aufgenommenAm: a.beginn.toISOString(),
          zeitzone: eigeneZeitzone(),
          dauerMs: a.dauerMs,
          nachfrageId: antwortRef.current?.id,
        })
        // Die Aufnahme beantwortet die Frage; gilt nur für diese eine
        if (antwortRef.current) {
          antwortRef.current = null
          setAntwort(null)
          setNachfrageVersion((v) => v + 1)
        }
        setWartend(await warteschlange.alle())
        await hochladen()
      },
      [hochladen],
    ),
  )

  // Aufruf über #aufnehmen (auch bei schon offener App): Aufnahme sofort starten. Hat der Browser das Mikrofon noch nicht
  // dauerhaft erlaubt, fragt er einmal nach; danach startet es ohne Tippen.
  const starten = aufnahme.starten
  const startenRef = useRef(starten)
  startenRef.current = starten
  useEffect(() => {
    if (!sofortAufnehmen) return
    onAufnehmenGestartet()
    startenRef.current()
  }, [sofortAufnehmen, onAufnehmenGestartet])

  const aelterLaden = async () => {
    const aeltest = notizen[notizen.length - 1]
    const seite = await api.notizen(aeltest?.aufgenommenAm, filter)
    if (aktuellerFilter.current !== filter) return
    setNotizen((alt) => zusammenfuehren(alt, seite.notizen))
    setWeitere(seite.weitere)
  }

  // Archivierte verschwinden aus dem Tagebuch, zurückgeholte aus dem Archiv
  const ersetzen = (n: Notiz) =>
    setNotizen((alt) => (n.archiviert !== !!filter.archiv ? alt.filter((x) => x.id !== n.id) : alt.map((x) => (x.id === n.id ? n : x))))
  const entfernen = (id: number) => setNotizen((alt) => alt.filter((x) => x.id !== id))

  const textSpeichern = async (text: string) => {
    const n = await api.textNotiz({ clientId: crypto.randomUUID(), aufgenommenAm: new Date().toISOString(), zeitzone: eigeneZeitzone(), text, nachfrageId: antwort?.id })
    if (!filter.archiv) setNotizen((alt) => zusammenfuehren(alt, [n]))
    setTippen(false)
    if (antwort) {
      setAntwort(null)
      setNachfrageVersion((v) => v + 1)
    }
  }

  const tage = useMemo(() => nachTagen(notizen, new Date(), eigeneZeitzone()), [notizen])

  return (
    <div className="tagebuch">
      {!kiBereit && (
        <p className="hinweis">Die KI-API ist nicht eingerichtet. Aufnahmen werden gespeichert und transkribiert, sobald sie verfügbar ist.</p>
      )}
      {filter.archiv && (
        <p className="filter">
          <strong>Archivierte Notizen</strong>
          <a href="#tagebuch" aria-label="Zurück zum Tagebuch">
            ✕
          </a>
        </p>
      )}
      {filterName && !filter.archiv && (
        <p className="filter">
          Nur Notizen mit <strong>{filterName}</strong>
          <a href="#tagebuch" aria-label="Filter entfernen">
            ✕
          </a>
        </p>
      )}
      {!filterName && (
        <p className="geteilt-link">
          <a href="#geteilt">
            💌 Von Freunden geteilt{neueGeteilte > 0 && <strong> · {neueGeteilte} neu</strong>}
          </a>
          <a href={link.archiv}>🗄 Archiv</a>
        </p>
      )}
      {fehler && <p className="hinweis fehler">{fehler}</p>}
      {!filterName && !filter.archiv && (
        <Nachfrage
          version={nachfrageVersion}
          antwortAuf={antwort?.id ?? null}
          onSprechen={(f) => setAntwort(f)}
          onTippen={(f) => {
            setAntwort(f)
            setTippen(true)
          }}
          onAbbrechen={() => {
            setAntwort(null)
            setTippen(false)
          }}
        />
      )}
      {tippen && (
        <TextEingabe
          frage={antwort?.frage}
          onSpeichern={textSpeichern}
          onAbbrechen={() => {
            setTippen(false)
            setAntwort(null)
          }}
        />
      )}

      {wartend.length > 0 && (
        <section className="tag">
          <h2>Noch nicht hochgeladen</h2>
          {wartend.map((w) => (
            <WartendKarte
              key={w.clientId}
              wartend={w}
              onVerwerfen={async () => {
                await warteschlange.entfernen(w.clientId)
                setWartend(await warteschlange.alle())
              }}
            />
          ))}
        </section>
      )}

      {tage.map((tag) => (
        <section className="tag" key={tag.datum}>
          <h2>{tag.titel}</h2>
          {tag.notizen.map((n) => (
            <NotizKarte key={n.id} notiz={n} kiBereit={kiBereit} onGeaendert={ersetzen} onGeloescht={entfernen} onRueckfragen={onAusgewertet} />
          ))}
        </section>
      ))}

      {geladen && tage.length === 0 && filter.archiv && (
        <div className="platzhalter">
          <p>Keine archivierten Notizen.</p>
          <p className="leise">Archivierte Notizen verschwinden aus dem Tagebuch, bleiben aber Quelle für Fragen und die Kartei.</p>
        </div>
      )}
      {geladen && tage.length === 0 && wartend.length === 0 && !filterName && (
        <div className="platzhalter">
          <p>Noch keine Notizen.</p>
          <p className="leise">Tippe auf den roten Knopf und erzähl, was du dir merken möchtest.</p>
        </div>
      )}
      {weitere && (
        <button className="zweitrangig mittig" onClick={aelterLaden}>
          Ältere Notizen laden
        </button>
      )}

      <AufnahmeLeiste aufnahme={aufnahme} onTippen={() => {
          setAntwort(null)
          setTippen(true)
        }} />
    </div>
  )
}
